import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { GlobalSearchService, SearchResults, SearchResult } from '@core/services/navigation/global-search.service';

@Component({
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatRippleModule,
        MatProgressSpinnerModule,
        ReactiveFormsModule,
        TranslatePipe
    ],
    selector: 'vex-search-modal',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div class="max-h-96 overflow-hidden flex flex-col">
      <!-- Search Input -->
      <div class="flex items-center gap-4 px-6 py-3 border-b border-divider">
        <mat-icon
          svgIcon="mat:search"
        class="text-secondary flex-none"></mat-icon>
        <input
          [formControl]="searchControl"
          type="text"
          [placeholder]="'shared.searchModal.placeholder' | translate"
          class="text-xl font-medium bg-transparent outline-none flex-auto placeholder-secondary" />
        @if (isLoading()) {
          <mat-progress-spinner
            diameter="20"
            mode="indeterminate"
            class="flex-none">
          </mat-progress-spinner>
        }
      </div>
    
      <!-- Search Results -->
      @if (searchResults$ | async; as results) {
        <div class="flex-1 overflow-y-auto">
          <!-- No Results -->
          @if (searchControl.value && searchControl.value.length >= 2 && results.totalCount === 0 && isLoading() === false) {
            <div
              class="p-6 text-center text-secondary">
              <mat-icon svgIcon="mat:search_off" class="text-4xl mb-2 opacity-50"></mat-icon>
              <div class="text-base font-medium">{{ 'shared.searchModal.noResultsTitle' | translate }}</div>
              <div class="text-sm">{{ 'shared.searchModal.noResultsHint' | translate }}</div>
            </div>
          }
          <!-- Properties Section -->
          @if (results.properties.length > 0) {
            <div class="p-4">
              <div class="text-xs font-semibold text-secondary px-2 mb-2 flex items-center gap-2">
                <mat-icon svgIcon="mat:home" class="icon-xs text-green-600"></mat-icon>
                {{ 'shared.searchModal.properties' | translate:{ count: results.properties.length } }}
              </div>
              <div class="space-y-1">
                @for (item of results.properties; track item) {
                  <div tabindex="0"
                    (click)="navigateToResult(item)" (keydown.enter)="navigateToResult(item)"
                    class="px-2 py-2 hover:bg-hover rounded transition duration-200 ease-out flex items-center gap-4 cursor-pointer select-none"
                    matRipple>
                    <div class="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                      <mat-icon [svgIcon]="'mat:' + item.icon" class="icon-sm flex-none text-green-600"></mat-icon>
                    </div>
                    <div class="flex-auto text-base font-medium min-w-0">
                      <div class="truncate">{{item.title}}</div>
                      <div class="text-secondary text-xs truncate">{{item.subtitle}}</div>
                    </div>
                    <mat-icon svgIcon="mat:chevron_right" class="icon-sm flex-none"></mat-icon>
                  </div>
                }
              </div>
            </div>
          }
          <!-- Tenants Section -->
          @if (results.tenants.length > 0) {
            <div class="p-4">
              <div class="text-xs font-semibold text-secondary px-2 mb-2 flex items-center gap-2">
                <mat-icon svgIcon="mat:people" class="icon-xs text-purple-600"></mat-icon>
                {{ 'shared.searchModal.tenants' | translate:{ count: results.tenants.length } }}
              </div>
              <div class="space-y-1">
                @for (item of results.tenants; track item) {
                  <div tabindex="0"
                    (click)="navigateToResult(item)" (keydown.enter)="navigateToResult(item)"
                    class="px-2 py-2 hover:bg-hover rounded transition duration-200 ease-out flex items-center gap-4 cursor-pointer select-none"
                    matRipple>
                    <div class="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100">
                      <mat-icon [svgIcon]="'mat:' + item.icon" class="icon-sm flex-none text-purple-600"></mat-icon>
                    </div>
                    <div class="flex-auto text-base font-medium min-w-0">
                      <div class="truncate">{{item.title}}</div>
                      <div class="text-secondary text-xs truncate">{{item.subtitle}}</div>
                    </div>
                    <mat-icon svgIcon="mat:chevron_right" class="icon-sm flex-none"></mat-icon>
                  </div>
                }
              </div>
            </div>
          }
          <!-- Contracts Section -->
          @if (results.contracts.length > 0) {
            <div class="p-4">
              <div class="text-xs font-semibold text-secondary px-2 mb-2 flex items-center gap-2">
                <mat-icon svgIcon="mat:description" class="icon-xs text-blue-600"></mat-icon>
                {{ 'shared.searchModal.contracts' | translate:{ count: results.contracts.length } }}
              </div>
              <div class="space-y-1">
                @for (item of results.contracts; track item) {
                  <div tabindex="0"
                    (click)="navigateToResult(item)" (keydown.enter)="navigateToResult(item)"
                    class="px-2 py-2 hover:bg-hover rounded transition duration-200 ease-out flex items-center gap-4 cursor-pointer select-none"
                    matRipple>
                    <div class="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                      <mat-icon [svgIcon]="'mat:' + item.icon" class="icon-sm flex-none text-blue-600"></mat-icon>
                    </div>
                    <div class="flex-auto text-base font-medium min-w-0">
                      <div class="truncate">{{item.title}}</div>
                      <div class="text-secondary text-xs truncate">{{item.subtitle}}</div>
                    </div>
                    <mat-icon svgIcon="mat:chevron_right" class="icon-sm flex-none"></mat-icon>
                  </div>
                }
              </div>
            </div>
          }
          <!-- Loans Section -->
          @if (results.loans.length > 0) {
            <div class="p-4">
              <div class="text-xs font-semibold text-secondary px-2 mb-2 flex items-center gap-2">
                <mat-icon svgIcon="mat:account_balance" class="icon-xs text-orange-600"></mat-icon>
                {{ 'shared.searchModal.loans' | translate:{ count: results.loans.length } }}
              </div>
              <div class="space-y-1">
                @for (item of results.loans; track item) {
                  <div tabindex="0"
                    (click)="navigateToResult(item)" (keydown.enter)="navigateToResult(item)"
                    class="px-2 py-2 hover:bg-hover rounded transition duration-200 ease-out flex items-center gap-4 cursor-pointer select-none"
                    matRipple>
                    <div class="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100">
                      <mat-icon [svgIcon]="'mat:' + item.icon" class="icon-sm flex-none text-orange-600"></mat-icon>
                    </div>
                    <div class="flex-auto text-base font-medium min-w-0">
                      <div class="truncate">{{item.title}}</div>
                      <div class="text-secondary text-xs truncate">{{item.subtitle}}</div>
                    </div>
                    <mat-icon svgIcon="mat:chevron_right" class="icon-sm flex-none"></mat-icon>
                  </div>
                }
              </div>
            </div>
          }
          <!-- Initial State -->
          @if (!searchControl.value || searchControl.value.length < 2) {
            <div
              class="p-6 text-center text-secondary">
              <mat-icon svgIcon="mat:search" class="text-4xl mb-2 opacity-50"></mat-icon>
              <div class="text-base font-medium">{{ 'shared.searchModal.initialTitle' | translate }}</div>
              <div class="text-sm">{{ 'shared.searchModal.initialHint' | translate }}</div>
            </div>
          }
        </div>
      }
    </div>
    `,
    styles: [``]
})
export class SearchModalComponent implements OnInit, OnDestroy {
  searchControl = new FormControl('');
  private destroy$ = new Subject<void>();
  
  searchResults$: Observable<SearchResults>;
  readonly isLoading = signal<boolean>(false);

  constructor(
    private globalSearchService: GlobalSearchService,
    private router: Router,
    private dialogRef: MatDialogRef<SearchModalComponent>
  ) {
    // Set up reactive search
    this.searchResults$ = this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.length < 2) {
          this.isLoading.set(false);
          return this.globalSearchService.search('');
        }

        this.isLoading.set(true);
        return this.globalSearchService.search(query).pipe(
          switchMap(results => {
            this.isLoading.set(false);
            return [results];
          })
        );
      }),
      takeUntil(this.destroy$)
    );
  }

  ngOnInit(): void {
    // Focus the input when modal opens
    setTimeout(() => {
      const input = document.querySelector('vex-search-modal input[type="text"]') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToResult(result: SearchResult): void {
    this.dialogRef.close();
    void this.router.navigate([result.route]);
  }
}
